import React, { useState, useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { Icon } from '../icons';

const HEADLINE = 'Attivazioni, licenze e aggiornamenti in un unico posto.';

// Spezza il titolo in parole, ciascuna avvolta in una maschera
// (overflow:hidden) cosi' l'entrata GSAP le fa "salire" da sotto invece di
// un semplice fade: gli spazi restano testo normale fuori dalla maschera,
// cosi' l'a-capo del browser continua a funzionare naturalmente.
function splitWords(text) {
  const words = text.split(' ');
  return words.map((word, i) => (
    <React.Fragment key={i}>
      <span className="word-mask"><span className="word-inner">{word}</span></span>
      {i < words.length - 1 ? ' ' : ''}
    </React.Fragment>
  ));
}

function Login({ onLogin, onForgot, flash }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const heroRef = useRef(null);
  const topRef = useRef(null);
  const subRef = useRef(null);
  const listRef = useRef(null);

  // Entrata del testo della hero: un'unica timeline GSAP (invece delle 3
  // animazioni CSS scaglionate a mano) cosi' il ritmo e' coordinato in un
  // punto solo. useLayoutEffect + gsap.set nascondono gli elementi PRIMA del
  // primo paint, evitando un flash del contenuto gia' visibile.
  useLayoutEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const words = hero.querySelectorAll('.word-inner');
    const listItems = listRef.current ? listRef.current.children : [];

    const ctx = gsap.context(() => {
      if (reduce) {
        gsap.set([topRef.current, words, subRef.current, listItems], { clearProps: 'all' });
        return;
      }
      gsap.set(topRef.current, { y: 14, opacity: 0 });
      gsap.set(words, { yPercent: 110, opacity: 0 });
      gsap.set(subRef.current, { y: 10, opacity: 0 });
      gsap.set(listItems, { y: 12, opacity: 0 });

      gsap.timeline({ defaults: { ease: 'power3.out' } })
        .to(topRef.current, { y: 0, opacity: 1, duration: 0.5 })
        .to(words, { yPercent: 0, opacity: 1, duration: 0.7, stagger: 0.045 }, '-=0.25')
        .to(subRef.current, { y: 0, opacity: 1, duration: 0.5 }, '-=0.35')
        .to(listItems, { y: 0, opacity: 1, duration: 0.45, stagger: 0.08 }, '-=0.25');
    }, hero);

    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Errore di autenticazione'); return; }
      onLogin(data);
    } catch {
      setError('Impossibile contattare il server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap dark">
      <div className="login-hero" ref={heroRef}>
        <div className="login-hero-top" ref={topRef}>
          <div className="login-logo"><Icon name="logo" size={22} strokeWidth={2} /></div>
          <div className="login-brand-text" style={{ color: '#fff' }}>
            <strong style={{ display: 'block' }}>HIPLAAS</strong>
            <span>Manager</span>
          </div>
        </div>
        <div className="login-hero-mid">
          <h2>{splitWords(HEADLINE)}</h2>
          <p ref={subRef}>Gestisci codici di attivazione, licenze firmate legate all'hardware e la distribuzione degli installer HIPLAAS.</p>
        </div>
        <div className="login-hero-list" ref={listRef}>
          <div><span className="login-hero-icon"><Icon name="key" size={16} /></span>Codici di attivazione monouso per area</div>
          <div><span className="login-hero-icon"><Icon name="shield" size={16} /></span>Licenze firmate Ed25519 anti-copia (2 su 3)</div>
          <div><span className="login-hero-icon"><Icon name="upload" size={16} /></span>Canale aggiornamenti con storage integrato</div>
        </div>
      </div>

      <div className="login-panel">
        <form className="login-card" onSubmit={handleSubmit}>
          <h1>Bentornato</h1>
          <p className="subtitle">Accedi al portale di gestione</p>

          {flash && !error && <div className="msg msg-success" style={{ marginBottom: '1rem' }}><Icon name="check" size={16} />{flash}</div>}
          {error && <div className="msg msg-error" style={{ marginBottom: '1rem' }}><Icon name="alert" size={16} />{error}</div>}

          <div className="field">
            <label htmlFor="username">Username</label>
            <input id="username" className="input" type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Inserisci username" autoComplete="username" required autoFocus />
          </div>
          <div className="field">
            <label htmlFor="password">Password</label>
            <div className="input-with-action">
              <input id="password" className="input" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Inserisci password" autoComplete="current-password" required />
              <button type="button" className="input-action" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}>
                <Icon name={showPassword ? 'eyeOff' : 'eye'} size={17} />
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-block" disabled={loading} style={{ marginTop: '0.5rem' }}>
            {loading ? 'Accesso in corso…' : 'Accedi'}
          </button>
          {onForgot && (
            <div style={{ textAlign: 'center', marginTop: '0.85rem' }}>
              <button type="button" className="link-btn" onClick={onForgot}>Password dimenticata?</button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Login;
